import { useState } from 'react';
import {
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';

import { useAdminGetUsers } from '../../hooks/admin-get-users.hook';
import { useDebounce } from '../../../shared/hooks/use-debounce.hook';
import { User } from '../../types/user.type';
import { UserRole } from '../../types/user-role.type';
import { USER_ROLE_LABELS } from '../../constants/user-role-labels.constants';

type ContactSearchTableProps = {
  selectedContactId: string | null;
  onSelectContact: (contact: User) => void;
  maxHeight?: string;
};

const useStyles = makeStyles({
  filters: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '0.5rem',
  },
  search: {
    flex: 1,
  },
  role: {
    width: 220,
  },
});

export function ContactSearchTable({
  selectedContactId,
  onSelectContact,
  maxHeight = '40svh',
}: ContactSearchTableProps) {
  const classes = useStyles();

  const [search, setSearch] = useState('');
  const [role, setRole] = useState<UserRole | ''>('');
  const debouncedSearch = useDebounce(search, 500);

  const { isLoading, isRefetching, users } = useAdminGetUsers({
    page: 1,
    limit: 10,
    sortBy: 'lastname',
    sortDirection: 'asc',
    filters: {
      search: debouncedSearch || undefined,
      role: role || undefined,
    },
  });

  const isFetching = isLoading || isRefetching;
  const hasUsers = !!users.length;

  return (
    <>
      <section className={classes.filters}>
        <TextField
          className={classes.search}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          size="small"
          label="Rechercher un contact"
          placeholder="Email ou nom complet"
          variant="outlined"
        />
        <FormControl variant="outlined" size="small" className={classes.role}>
          <InputLabel>Rôle</InputLabel>
          <Select label="Rôle" value={role} onChange={(event) => setRole(event.target.value as UserRole | '')}>
            <MenuItem value="">Tous les rôles</MenuItem>
            {Object.entries(USER_ROLE_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </section>
      <Paper>
        <TableContainer style={{ minHeight: maxHeight, maxHeight }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 'bold' }}>Prénom</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Nom</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Email</TableCell>
                <TableCell style={{ fontWeight: 'bold' }}>Téléphone</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetching && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Skeleton variant="rect" animation="wave" height={50} />
                  </TableCell>
                </TableRow>
              )}
              {!isFetching && !hasUsers && (
                <TableRow>
                  <TableCell colSpan={4}>Aucun utilisateur trouvé</TableCell>
                </TableRow>
              )}
              {!isFetching &&
                hasUsers &&
                users.map((user) => (
                  <TableRow
                    key={user.id}
                    hover
                    selected={user.id === selectedContactId}
                    onClick={() => onSelectContact(user)}
                    style={{ cursor: 'pointer' }}
                  >
                    <TableCell>{user.firstname}</TableCell>
                    <TableCell>{user.lastname}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phoneNumber}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
}
